import type { NetworkAllCampaigns } from '@common/types/campaign';
import { NetworkSessionUser, NetworkLogout } from '@campaign/types/network';
import { api, apiWithoutAuthentication } from '@campaign/utils/api';

const PCC_IDPF_ENDPOINT = import.meta.env.PCC_IDPF_ENDPOINT;
const PCC_FRONTEND_SYSTEM_ID = import.meta.env.PCC_FRONTEND_SYSTEM_ID;
const PCC_CAMPAIGN_SERVICE_ENDPOINT = import.meta.env
  .PCC_CAMPAIGN_SERVICE_ENDPOINT;
const PCC_CAMPAIGN_CUSTOMER_URL = import.meta.env.PCC_CAMPAIGN_CUSTOMER_URL;

/**
 * Request a new access token
 *
 * @returns access token
 */
export async function requestNewAccessToken(): Promise<{
  accessToken: string;
}> {
  const url = new URL(`${PCC_IDPF_ENDPOINT}/refresh/subdomain`);
  const payload = { frontsystemId: Number(PCC_FRONTEND_SYSTEM_ID) };

  return apiWithoutAuthentication
    .post(url.href, {
      body: JSON.stringify(payload),
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
      },
    })
    .json();
}

/**
 * Get session user data from IDPF
 *
 * @returns NetworkSessionUser
 */
export async function getSessionUser(): Promise<NetworkSessionUser> {
  const url = new URL(`${PCC_IDPF_ENDPOINT}/user`);

  return api.get(url.href).json();
}

/**
 * Get all campaign data from the Campaign API
 *
 * @param isLoggedIn - whether the user is logged in
 * @param limit - number of campaigns to fetch
 * @param offset - pagination offset
 * @returns NetworkAllCampaigns
 */
export async function getCampaigns(
  isLoggedIn: boolean,
  limit: number,
  offset = 0
): Promise<NetworkAllCampaigns> {
  const url = new URL(
    `${PCC_CAMPAIGN_SERVICE_ENDPOINT}/meiji/customer/campaigns?limit=${limit}&offset=${offset}`
  );

  // Use authenticated API if user is logged in
  const apiClient = isLoggedIn ? api : apiWithoutAuthentication;

  return apiClient.get(url.href).json();
}

/**
 * Get user campaign data from the Campaign API
 *
 * @param limit - number of campaigns to fetch
 * @param offset - pagination offset
 * @returns user's NetworkAllCampaigns
 */
export async function getUserCampaigns(
  limit: number,
  offset = 0
): Promise<NetworkAllCampaigns> {
  const url = new URL(
    `${PCC_CAMPAIGN_SERVICE_ENDPOINT}/meiji/customer/user/campaigns?limit=${limit}&offset=${offset}`
  );

  return api.get(url.href).json();
}

/**
 * Request logout to IDPF
 *
 * MEIJI-3814
 */
export async function logout(): Promise<NetworkLogout> {
  const url = new URL(`${PCC_IDPF_ENDPOINT}/logout/subdomain`);
  const payload = {
    frontsystemId: Number(PCC_FRONTEND_SYSTEM_ID),
    logoutDestination: `${PCC_CAMPAIGN_CUSTOMER_URL}/`,
  };

  return await api
    .post(url.href, {
      body: JSON.stringify(payload),
      credentials: 'include',
    })
    .json<NetworkLogout>();
}
